import React, {
  FC,
  createContext,
  ReactNode,
  useRef,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { Elements } from '@stripe/react-stripe-js';

import { useDispatch, useSelector } from "react-redux";

import { useAppSelector } from "@web-solutions/store/types";

import { PaymentProcessorRef } from "core/payment/payment-processor-provider/components";
import { OneTimePaymentRef } from "core/one-time/constans/types";
import { init } from "core/store/billing/actions";
import { getPublicKeys } from '@web-solutions/react-billing';

import {
  billingSelectors,
  selectPaymentProject,
  selectPaymentSystem,
} from "core/store/billing/selectors";
import { PaymentSystem } from '@web-solutions/react-billing';

import { profileSelectors } from "core/store/profile/selectors";

import { PaymentUpsale } from "./payments/upsale-payment";
import { PaymentSubscription } from "./payments/subscription-payment";
import { upsaleSelectors } from "core/upsale/store/selectors";
import { routingSelectors } from "core/store/routing/selectors";
import { useStore } from "../../../../apps/virtual-brainary/src/onboarding/store/store";
import { ensureAnonEmail } from "../../../../apps/virtual-brainary/src/onboarding/helpers/anonEmail";
import { useRemoteConfig } from "core/hooks/use-remote-config";
import { processEmail } from "core/store/profile/actions";
import { reInit as reInitRemoteConfig } from '@web-solutions/core/store/remote-config/actions';
import { Options } from "./components/payment-processor";
import Billing from '@web-solutions/module-billing';

export type CallBacksType = {
  onSuccess?: (data: any) => void;
  onSuccessUpsale?: (data: any) => void;
  onError?: (data: any) => void;
  onClose?: (data: any) => void;
};

type PaymentProcessorContextType = {
  ppRef: React.RefObject<PaymentProcessorRef | null>;
  otpRef: React.RefObject<OneTimePaymentRef | null>;
  callbacksRef: React.RefObject<CallBacksType | null>;
  options: Options | null;
  setOptions: (options: Options) => void;
};

export const PaymentProcessorContext =
  createContext<PaymentProcessorContextType | null>(null);

type PropsProvider = {
  children: ReactNode;
};

export const PaymentProcessorProvider: FC<PropsProvider> = ({ children }) => {
  const ppRef = useRef<PaymentProcessorRef | null>(null);
  const otpRef = useRef<OneTimePaymentRef | null>(null);
  const callbacksRef = useRef<CallBacksType | null>({});

  const authorized = useAppSelector(profileSelectors.authorized);
  const isStartUpsaleFlow = useAppSelector(upsaleSelectors.isStartUpsaleFlow);
  const isInitBilling = useAppSelector(billingSelectors.isInitBilling);
  const paymentSystem = useSelector(selectPaymentSystem);
  const paymentProject = useSelector(selectPaymentProject);
  const startRoute = useAppSelector(routingSelectors.startRoute);
  const email = useStore((state) => state.email);
  const anonEmail = useStore((state) => state.anonEmail);
  const [options, setOptions] = useState<Options | null>(null);

  // @ts-ignore — collectEmailAfterPayment lives on the core RemoteConfig type
  const { collectEmailAfterPayment } = useRemoteConfig();

  const DEFAULT_EMAIL = "default@voery.app";

  const dispatch = useDispatch();

  // Truegate deferred-email flow: the truegate billing backend keys the customer
  // by email, so instead of a shared default we create it with a synthetic
  // per-user address and collect the real email afterwards on the
  // `email-after-payment` page. Stripe keeps its own anonymous-customer flow.
  const isTruegateDeferredEmail =
    Boolean(collectEmailAfterPayment) && paymentSystem === PaymentSystem.TRUEGATE;

  useEffect(() => {
    if (isTruegateDeferredEmail && !email && !anonEmail) {
      ensureAnonEmail();
    }
  }, [isTruegateDeferredEmail, email, anonEmail]);

  const resolvedEmail =
    email || (isTruegateDeferredEmail ? anonEmail : DEFAULT_EMAIL);

  const params = new URLSearchParams(window.location.search);
  const flowParam = params.get("flow");
  const isSubscriptionFlow = flowParam === "subscription";
  const isChangePaymentMethod = flowParam === "change-payment-method";

  // Determine product type for Stripe: subscription (dynamic-personal-plan) or onetime (upsale-dynamic)
  const isUpsaleFlow = useAppSelector(upsaleSelectors.isStartUpsaleFlow);
  const stripeProductType = isUpsaleFlow ? 'onetime' : 'subscription';

  useEffect(() => {
    console.log('[PAYMENT PROCESSOR] Init check:', {
      paymentSystem,
      paymentProject,
      isInitBilling,
      resolvedEmail,
      isSubscriptionFlow,
      isChangePaymentMethod,
      email,
      isStripeAndNoEmail: paymentSystem === PaymentSystem.STRIPE && !email,
    });

    if (
      paymentSystem &&
      paymentProject &&
      !isInitBilling &&
      resolvedEmail &&
      !isSubscriptionFlow &&
      !isChangePaymentMethod &&
      (paymentSystem !== PaymentSystem.STRIPE || email)
    ) {
      dispatch(processEmail(resolvedEmail));

      dispatch(reInitRemoteConfig());

      const initResult = dispatch(init());
      console.log('[ PROCESSOR] init() dispatched, result:', initResult);
    } else {
      console.log('[PAYMENT PROCESSOR] Conditions NOT met!');
    }
  }, [
    dispatch,
    paymentSystem,
    paymentProject,
    isInitBilling,
    resolvedEmail,
    isSubscriptionFlow,
    isChangePaymentMethod,
    email
  ]);

  const stripePromiseRef = useRef<any>(Promise.resolve(null));

  useEffect(() => {
    // Only initialize once
    if (stripePromiseRef.current !== Promise.resolve(null)) {
      return;
    }

    const existingStripeInstance = (window as any).__stripe_instance__;
    const stripePublicKey = getPublicKeys()?.stripe?.public_key;
    const stripeFactory = (window as any).Stripe;

    if (paymentSystem === PaymentSystem.STRIPE) {
      // Use already initialized Stripe instance from email page
      if (existingStripeInstance) {
        stripePromiseRef.current = Promise.resolve(existingStripeInstance);
      } else if (stripePublicKey && stripeFactory) {
        // Fallback: initialize Stripe if not already done (for non-email-flow routes)
        try {
          stripePromiseRef.current = Promise.resolve(stripeFactory(stripePublicKey));
        } catch (e) {
          console.error('[PAYMENT PROCESSOR] Failed to initialize Stripe:', e);
        }
      }
    } else if (paymentSystem) {
      // For non-Stripe systems (TRUEGATE, etc), we still need to provide a null promise
      // to avoid Elements context errors in CardForm
      console.log('[PAYMENT PROCESSOR] Non-Stripe payment system, setting null stripe promise');
      // Keep the initial Promise.resolve(null)
    }
  }, [paymentSystem]);

  // Note: Stripe products are already fetched on email page, no need to fetch again here
  // useEffect(() => {
  //   if (paymentSystem === PaymentSystem.STRIPE && isInitBilling) {
  //     console.log('[PAYMENT PROCESSOR] Fetching Stripe products with type:', stripeProductType);
  //     Billing.getStripeProducts(stripeProductType)
  //       .then((products) => {
  //         console.log('[PAYMENT PROCESSOR] Got Stripe products:', products);
  //       })
  //       .catch((error) => {
  //         console.error('[PAYMENT PROCESSOR] Error fetching Stripe products:', error);
  //       });
  //   }
  // }, [paymentSystem, isInitBilling, stripeProductType]);

  const content = (
    <>
      {children}

      {isInitBilling && !isChangePaymentMethod &&
        (isStartUpsaleFlow ? (
          <PaymentUpsale
            localOtpRef={otpRef}
            ppRef={ppRef}
            callbacksRef={callbacksRef}
          />
        ) : (
          <PaymentSubscription localPpRef={ppRef} callbacksRef={callbacksRef} />
        ))}
    </>
  );

  return (
    <PaymentProcessorContext.Provider
      value={{
        ppRef,
        otpRef,
        callbacksRef,
        options,
        setOptions
      }}
    >
      <Elements stripe={stripePromiseRef.current}>
        {content}
      </Elements>
    </PaymentProcessorContext.Provider>
  );
};

export const usePayment = () => {
  const ctx = useContext(PaymentProcessorContext);

  if (!ctx) {
    throw new Error(
      "usePaymentProcessorRef must be used within PaymentProcessorProvider"
    );
  }

  return ctx;
};
